import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import LocalAirportIcon from '@material-ui/icons/LocalAirport';
import HotelIcon from '@material-ui/icons/Hotel';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import PeopleIcon from '@material-ui/icons/People';
import WorkIcon from '@material-ui/icons/Work';
import StarIcon from '@material-ui/icons/Star';

/**
 * Get meal plan name in Hebrew
 */
const getMealPlanName = (code, t) => {
  switch (code) {
    case 'HM': return t('hotel.mealPlans.halfBoard');
    case 'MB': return t('hotel.mealPlans.halfBoardWithDrinks');
    case 'MP': return t('hotel.mealPlans.halfBoard');
    case 'PC': return t('hotel.mealPlans.fullBoard');
    case 'PV': return t('hotel.mealPlans.fullBoardWithDrinks');
    case 'SA': return '';
    case 'SP': return '';
    case 'TI': return t('hotel.mealPlans.allInclusive');
    case 'TP': return t('hotel.mealPlans.allInclusivePremium');
    case 'SC': return '';
    case 'HD': return t('hotel.mealPlans.breakfast');
    default: return '';
  }
};

/**
 * Get baggage category name in Hebrew
 */
const getBaggageCategoryName = (code, t) => {
  switch (code) {
    case 'T': return t('reservation.steps.baggageSelection.categories.trolley');
    case 'C': return t('reservation.steps.baggageSelection.categories.suitcase');
    case 'S': return t('reservation.steps.baggageSelection.categories.backpack');
    default: return '';
  }
};

/**
 * StepOrderSummary - Step 4: Order review (read-only)
 */
const StepOrderSummary = ({
  packageDetails,
  quoteDetails,
  passengers = [],
  baggageState = [],
  totalPrice,
  onContinue,
  currency = 'USD'
}) => {
  const { t } = useTranslation();
  const data = packageDetails?.data;
  const currencySymbol = currency === 'USD' ? '$' : currency;

  // Generate star rating array
  const starsCount = data?.property?.stars || 0;
  const starsArray = Array(5).fill(false).map((_, i) => i < starsCount);

  // Calculate baggage total
  const baggageTotal = baggageState.reduce((sum, item) => sum + (item.baggage?.price || 0), 0);

  return (
    <div className="step-order-summary" role="region" aria-label={t('reservation.steps.orderSummary.title')}>
      {/* Flight Info */}
      <div className="order-summary-section" role="group" aria-labelledby="flight-info-title">
        <div className="order-summary-title" id="flight-info-title">
          <LocalAirportIcon className="order-summary-icon order-summary-icon--flight" aria-hidden="true" />
          {t('reservation.steps.orderSummary.flightDetails')}
        </div>

        <div className="order-summary-row">
          <span className="order-summary-label">{t('reservation.steps.orderSummary.route')}</span>
          <span className="order-summary-value">
            {data?.flight?.outbound?.departure_iata_name} - {data?.flight?.outbound?.arrival_iata_name} - {data?.flight?.inbound?.arrival_iata_name}
          </span>
        </div>

        <div className="order-summary-row">
          <span className="order-summary-label">{t('reservation.steps.orderSummary.dates')}</span>
          <span className="order-summary-value">
            {moment.utc(data?.departure?.date).format('D/MMM/YYYY')} - {moment.utc(data?.return?.date).format('D/MMM/YYYY')}
          </span>
        </div>

        <div className="order-summary-row">
          <span className="order-summary-label">{t('reservation.steps.orderSummary.duration')}</span>
          <span className="order-summary-value">
            {data?.nights} {t('common.labels.nights')}
          </span>
        </div>
      </div>

      {/* Hotel Info */}
      <div className="order-summary-section" role="group" aria-labelledby="hotel-info-title">
        <div className="order-summary-title" id="hotel-info-title">
          <HotelIcon className="order-summary-icon" aria-hidden="true" />
          {t('reservation.steps.orderSummary.hotelDetails')}
        </div>

        <div className="order-summary-row">
          <span className="order-summary-label">{t('reservation.steps.orderSummary.hotelName')}</span>
          <span className="order-summary-value">{data?.property?.name}</span>
        </div>

        <div className="order-summary-row">
          <span className="order-summary-label">{t('reservation.steps.orderSummary.rating')}</span>
          <span className="order-summary-value" aria-label={t('reservation.steps.orderSummary.stars', { count: starsCount })}>
            {starsArray.map((filled, idx) => (
              <StarIcon
                key={idx}
                className={`order-summary-star ${filled ? 'order-summary-star--filled' : ''}`}
                aria-hidden="true"
              />
            ))}
          </span>
        </div>

        {getMealPlanName(data?.property?.meal_code, t) && (
          <div className="order-summary-row">
            <span className="order-summary-label">{t('reservation.steps.orderSummary.boardBasis')}</span>
            <span className="order-summary-value order-summary-value--meal">
              {getMealPlanName(data?.property?.meal_code, t)}
            </span>
          </div>
        )}

        {data?.property?.room?.name && (
          <div className="order-summary-row">
            <span className="order-summary-label">{t('reservation.steps.orderSummary.roomType')}</span>
            <span className="order-summary-value">{data?.property?.room?.name}</span>
          </div>
        )}
      </div>


      {/* Passengers */}
      {passengers.length > 0 && (
        <div className="order-summary-section" role="group" aria-labelledby="passengers-info-title">
          <div className="order-summary-title" id="passengers-info-title">
            <PeopleIcon className="order-summary-icon" aria-hidden="true" />
            {t('reservation.steps.orderSummary.passengers')} ({passengers.length})
          </div>

          {passengers.filter(p => p && p.first_name).map((passenger, idx) => {
            const birthDate = passenger.birth_day && passenger.birth_month && passenger.birth_year
              ? moment(`${passenger.birth_year}-${passenger.birth_month}-${passenger.birth_day}`, 'YYYY-MM-DD')
              : null;
            return (
              <div key={idx} className="order-summary-row">
                <span className="order-summary-label">{t('reservation.steps.passengerDetails.passengerNumber', { number: idx + 1 })}</span>
                <span className="order-summary-value">
                  {passenger.first_name} {passenger.last_name}
                  {birthDate && birthDate.isValid() && (
                    <span className="order-summary-birthdate">
                      {` (${birthDate.locale('en').format('D MMM YYYY')})`}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Baggage */}
      {baggageState.length > 0 && (
        <div className="order-summary-section" role="group" aria-labelledby="baggage-info-title">
          <div className="order-summary-title" id="baggage-info-title">
            <WorkIcon className="order-summary-icon" aria-hidden="true" />
            {t('reservation.steps.orderSummary.baggage')}
          </div>

          {/* Group baggage by passenger, show outbound first then inbound */}
          {(() => {
            const paidBaggage = baggageState.filter(b => b.baggage?.price > 0);
            // Get unique passenger indices
            const passengerIndices = [...new Set(baggageState.map(b => b.passenger?.index || 0))].sort((a, b) => a - b);

            return passengerIndices.map(passengerIndex => {
              const passengerBaggage = baggageState.filter(b => (b.passenger?.index || 0) === passengerIndex);
              // Sort: outbound ('out') first, then inbound ('in')
              const sortedBaggage = passengerBaggage.sort((a, b) => {
                if (a.direction === 'out' && b.direction === 'in') return -1;
                if (a.direction === 'in' && b.direction === 'out') return 1;
                return 0;
              });

              // Group by direction for this passenger
              const outboundBaggage = sortedBaggage.filter(b => b.direction === 'out');
              const inboundBaggage = sortedBaggage.filter(b => b.direction === 'in');

              const formatBaggageList = (items) => {
                return items.map(item => {
                  const name = getBaggageCategoryName(item.baggage?.category?.id, t);
                  const weight = item.baggage?.weight?.amount;
                  const unit = item.baggage?.weight?.unit === 'kg' ? t('common.units.kg') : item.baggage?.weight?.unit;
                  return weight ? `${name} (${weight} ${unit})` : name;
                }).join(', ');
              };

              return (
                <div key={passengerIndex} className="order-summary-passenger-baggage">
                  <div className="order-summary-row">
                    <span className="order-summary-label order-summary-label--bold">{t('reservation.steps.passengerDetails.passengerNumber', { number: passengerIndex + 1 })}</span>
                  </div>
                  {outboundBaggage.length > 0 && (
                    <div className="order-summary-row order-summary-row--indent">
                      <span className="order-summary-label">{t('reservation.steps.orderSummary.outbound')}</span>
                      <span className="order-summary-value order-summary-value--direction">{formatBaggageList(outboundBaggage)}</span>
                    </div>
                  )}
                  {inboundBaggage.length > 0 && (
                    <div className="order-summary-row order-summary-row--indent">
                      <span className="order-summary-label">{t('reservation.steps.orderSummary.inbound')}</span>
                      <span className="order-summary-value order-summary-value--direction">{formatBaggageList(inboundBaggage)}</span>
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {1==2 && baggageTotal > 0 && (
            <div className="order-summary-row order-summary-row--total">
              <span className="order-summary-label order-summary-label--bold">{t('common.labels.totalPrice')} {t('reservation.steps.orderSummary.baggage')}</span>
              <span className="order-summary-value order-summary-value--total">
                {currencySymbol}{baggageTotal}
              </span>
            </div>
          )}
        </div>
      )}


      {/* Flex Booking Info */}
      {quoteDetails?.is_flex && (
        <div className="order-summary-section order-summary-section--flex" role="group" aria-labelledby="flex-info-title">
          <div className="order-summary-title order-summary-title--flex" id="flex-info-title">
            <VerifiedUserIcon className="order-summary-icon" aria-hidden="true" />
            {quoteDetails?.flex?.refund_amount > 0 ? t('reservation.steps.orderSummary.flexBooking') : t('reservation.steps.orderSummary.flexBookingWithCancellation')}
          </div>
          <p className="order-summary-flex-text">
            {t('reservation.steps.orderSummary.flexMessage', {
              time: quoteDetails?.flex?.expiration_hours < 24
                ? t('reservation.steps.orderSummary.flexHours', { count: quoteDetails?.flex?.expiration_hours })
                : t('reservation.steps.orderSummary.flexDays', { count: quoteDetails?.flex?.expiration_hours / 24 }),
              amount: quoteDetails?.flex?.refund_amount > 0
                ? `${quoteDetails?.flex?.refund_amount}${quoteDetails?.flex?.currency === 'USD' ? '$' : quoteDetails?.flex?.currency}`
                : t('reservation.steps.orderSummary.flexRefundDefault')
            })}
          </p>
        </div>
      )}

      {/* Continue Button */}
      <button
        type="button"
        className="step-continue-btn"
        onClick={onContinue}
        aria-label={t('common.buttons.approve')}
      >
        {t('common.buttons.approve')}
      </button>
    </div>
  );
};

export default StepOrderSummary;
